import CommonLookupDisplay from "components/CommonLookupDisplay";
import { GlobalContext, IModalFormElement } from "components/GlobalContext";
import { IColumnDefinition, createFilterableArrayColumnDefinition, createFilterableDateRangeColumnDefinition, createFilterableNumberRangeColumnDefinition } from "components/SortableTable";
import IOpportunity, { IOpportunityCreateOrUpdate, IOpportunityFilters, IOpportunityInitResult } from "models/sql/IOpportunity";
import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import useLocalStorage from "react-use-localstorage";
import OpportunityService from "services/OpportunityService";
import { formatCurrency, formatDate } from "utilities/StringUtility";
import { addNewLogEntry, applyGlobalFilterData, generateLog } from "utilities/MiscUtilities";
import { LinkContainer } from "react-router-bootstrap";
import { faEnvelope, faPhone, faListCheck, faMoneyCheck, faCartShopping } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import ProductService from "services/ProductService";
import IProduct from "models/sql/IProduct";

interface IOpportunitiesLookupProps {
    baseData?: IOpportunityInitResult;
}

const COUNTRIES_FILTER_KEY = "countries";
const STATES_FILTER_KEY = "states";

const OpportunitiesLookup = (props: IOpportunitiesLookupProps) => {
    const [filter, setFilter] = useState<IOpportunityFilters>({});
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const navigate = useNavigate();
    const [products, setProducts] = useState<IProduct[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [mode, setMode] = useLocalStorage(
        "opportunitiesLookupMode",
        searchParams.get("leads") === "true" ?
            "Leads" :
            searchParams.get("leads") === "false" ?
                "Opportunities" :
                searchParams.get("leads") === "closed" ?
                    "Closed" :
                    "");

    var getBaseFilter = () => {
        var result: any = {
            sortBys: ["OpportunityId"],
            sortDescending: true
        };

        switch (mode) {
            case "Leads":
                result = { ...result, leads: true, stages: undefined };
                break;
            case "Opportunities":
                result = { ...result, leads: false, stages: undefined };
                break;
            case "Closed":
                result = { ...result, stages: ["Closed Won", "Closed Lost"] };
                break;
            default:
                result = { ...result, stages: ["!Closed Won", "!Closed Lost"] };
                break;
        }

        return result;
    };

    useEffect(
        () => {
            (async () => {
                globalContextRef.current.setLoading(true);
                const accessToken = await globalContext.getAccessTokenAsync();
                const result =
                    await ProductService.GetAllAsync(
                        accessToken,
                        0,
                        999,
                        {});

                if (result.records != null) {
                    setProducts(result.records);
                }
                else {
                    globalContextRef.current.showErrorMessage("Unable to load Products");
                }

                globalContextRef.current.setLoading(false);
            })();
        }, []);

    useEffect(
        () => {
            if (JSON.stringify(filter) != "{}") {
                var newFilter = filter;
                switch (mode) {
                    case "Leads":
                        newFilter = { ...filter, leads: true };

                        if (newFilter.hasOwnProperty("stages")) {
                            delete newFilter.stages;
                        }

                        break;
                    case "Opportunities":
                        newFilter = { ...filter, leads: false };

                        if (newFilter.hasOwnProperty("stages")) {
                            delete newFilter.stages;
                        }

                        break;
                    case "Closed":
                        delete newFilter.leads;
                        newFilter = { ...filter, stages: ["Closed Won", "Closed Lost"] };
                        break;
                    default:
                        delete newFilter.leads;
                        newFilter = { ...newFilter, stages: ["!Closed Won", "!Closed Lost"] };
                        break;
                }

                setFilter(newFilter);
                setSearchParams(newFilter as any);
            }
        },
        [mode]);

    const showQuickNotes = (entry: IOpportunity, allData: IOpportunity[]) => {
        const formData: IModalFormElement[] = [{
            key: "Notes",
            type: "textarea",
            title: "Notes",
            value: entry.notes,
            style: { width: "100%", height: "200px" }
        }];

        globalContext.showModal({
            title: `Quick notes view`,
            cancelButtonTitle: "Close",
            formElements: formData,
            content: <></>,
            size: "lg",
            okButtonTitle: "Save Changes",
            okCallback: (data: any) => {
                let updateEntry = { ...entry };
                // @ts-ignore
                updateEntry.notes = data[0].value;
                updateEntry =
                    addNewLogEntry<IOpportunity>(
                        (new Date()),
                        `${globalContext.userName} updated notes`,
                        globalContext.userEmail,
                        101,
                        updateEntry);

                saveEntryUpdate(updateEntry, allData);
            }
        });
    };

    const showQuickLog = (entry: IOpportunity) => {
        globalContext.showModal({
            title: `Quick log view`,
            cancelButtonTitle: "Close",
            content: generateLog<IOpportunity>(entry),
            size: "lg"
        })
    };

    const saveEntryUpdate = async (entry: IOpportunity, allData: IOpportunity[]) => {
        globalContext.setLoading(true);
        const accessToken = await globalContext.getAccessTokenAsync();
        const data: IOpportunityCreateOrUpdate = {
            opportunityId: entry.opportunityId,
            created: entry.created,
            notes: entry.notes,
            extraData: entry.extraData,
            stage: entry.stage,
            country: entry.country,
            state: entry.state,
            fireDepartmentId: entry.fireDepartmentId,
            dealerId: entry.dealerId,
            meetingType: entry.meetingType,
            meetingStatus: entry.meetingStatus,
            meetingDate: entry.meetingDate,
            pocprimaryTitle: entry.pocprimaryTitle,
            pocprimaryName: entry.pocprimaryName,
            pocprimaryEmail: entry.pocprimaryEmail,
            pocprimaryPhone: entry.pocprimaryPhone,
            pocsecondaryTitle: entry.pocsecondaryTitle,
            pocsecondaryName: entry.pocsecondaryName,
            pocsecondaryEmail: entry.pocsecondaryEmail,
            pocsecondaryPhone: entry.pocsecondaryPhone,
            poctertiaryTitle: entry.poctertiaryTitle,
            poctertiaryName: entry.poctertiaryName,
            poctertiaryEmail: entry.poctertiaryEmail,
            poctertiaryPhone: entry.poctertiaryPhone,
            productInfoJson: entry.productInfoJson,
            healthRiskAssessmentId: entry.healthRiskAssessmentId,
            healthRiskAssessmentSentDate: entry.healthRiskAssessmentSentDate,
            airCardId: entry.airCardId,
            airCardSentDate: entry.airCardSentDate,
            fileAttachmentsJson: entry.fileAttachmentsJson,
            systemDataJson: entry.systemDataJson,
            lastUpdated: entry.lastUpdated,
            lastUpdatedBy: entry.lastUpdatedBy,
            createdBy: entry.createdBy,
            fundingStatus: entry.fundingStatus,
            salesRepId: entry.salesRepId,
            departmentName: entry.departmentName,
            station: entry.station,
            totalValue: entry.totalValue,
            qualified: entry.qualified,
            quotedTotalValue: entry.quotedTotalValue,
            quoteStatus: entry.quoteStatus,
            code: entry.code,
            source: entry.source,
            likleyhood: entry.likleyhood,
            timeline: entry.timeline,
            closureDate: entry.closureDate,
            fireStationId: entry.fireStationId
        };
        await OpportunityService.CreateOrUpdateAsync(accessToken, data);

        allData[allData.findIndex(x => x.opportunityId === data.opportunityId)] = data;
        globalContext.setLoading(false);
    };

    const exportTableDataAsync = async () => {
        const accessToken = await globalContext.getAccessTokenAsync();
        OpportunityService.DownloadExcelFileAsync(
            accessToken,
            "Opportunities.xlsx",
            applyGlobalFilterData(
                STATES_FILTER_KEY,
                COUNTRIES_FILTER_KEY,
                filter,
                globalContextRef.current.region,
                globalContextRef.current.organizationType));
    };

    const showQuote = (entry: IOpportunity, allData: IOpportunity[]) => {
        if (entry.systemDataJson) {
            const systemData = JSON.parse(entry.systemDataJson);
            const productList = systemData.productList;
            const publicProductList = systemData.publicProductList;

            globalContext.showModal({
                title: `Quick notes view`,
                cancelButtonTitle: "Close",
                content: <>
                    {
                        !!entry.totalValue &&
                        <Row>
                            <Col>
                                <h4>Internal</h4>
                                <ul style={{ listStyle: "none" }}>
                                    {
                                        (productList || []).map((p: any) =>
                                            <li key={p.productId}>{products.find(pr => pr.productId === p.productId)?.name || "N/A"} x {p.quantity} = {formatCurrency(p.total || 0)}</li>)
                                    }
                                    <li><strong>Total: {formatCurrency(entry.totalValue || 0)}</strong></li>
                                </ul>
                            </Col>
                        </Row>
                    }
                    {
                        !!entry.quotedTotalValue &&
                        <Row>
                            <Col>
                                <h4>Quoted</h4>
                                <ul style={{ listStyle: "none" }}>
                                    {
                                        (publicProductList || []).map((p: any) =>
                                            <li key={p.productId}>{products.find(pr => pr.productId === p.productId)?.name || "N/A"} x {p.quantity} = {formatCurrency(p.total || 0)}</li>)
                                    }
                                    <li><strong>{formatCurrency(entry.quotedTotalValue || 0)}</strong></li>
                                </ul>
                            </Col>
                        </Row>
                    }

                </>,
                size: "lg",
                okButtonTitle: "Close",
                okCallback: () => {
                }
            });
        }
    };

    const getExpectedClosure = (closureDate: string) => {
        if (!closureDate) {
            return "";
        }

        var months = moment(closureDate).diff(moment(), 'months', true);

        if (months < 1) {
            var days = moment(closureDate).diff(moment(), 'days', true);
            if (days === 1) {
                return "1 Day";
            }
            else if (days > 1) {
                return Math.floor(days) + " Days";
            }
        }
        else if (months === 1) {
            return Math.floor(months) + " Month";
        }
        else {
            return Math.floor(months) + " Months";
        }
    };

    const getColumns = (): IColumnDefinition<IOpportunity, IOpportunityFilters>[] => {
        return [
            {
                propName: "opportunityId",
                title: "Actions",
                customRenderer: (value, data, allData) => {
                    return (
                        <td key={`align-top actions${value}`}>
                            {
                                globalContext.roles.some(r => r === "Web.Admin") ?
                                    <>
                                        <span
                                            className="quick-action me-1"
                                            title="Quick note view"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                showQuickNotes(data, allData);
                                                return false;
                                            }}><FontAwesomeIcon icon={faMoneyCheck as IconProp} /></span>
                                        <span
                                            className="quick-action me-1"
                                            title="Activity log"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                showQuickLog(data);
                                                return false;
                                            }}><FontAwesomeIcon icon={faListCheck as IconProp} /></span>
                                    </> : <></>
                            }
                            {
                                data.pocprimaryEmail &&
                                <a
                                    className="me-2"
                                    href={`Mailto:${data.pocprimaryEmail}`}
                                    onClick={(e) => { e.stopPropagation(); }}>
                                    <FontAwesomeIcon icon={faEnvelope as IconProp} /></a>
                            }
                            {
                                data.pocprimaryPhone &&
                                <a
                                    className="me-2"
                                    href={`Tel:${data.pocprimaryPhone}`}
                                    onClick={(e) => { e.stopPropagation(); }}>
                                    <FontAwesomeIcon icon={faPhone as IconProp} /></a>
                            }
                            {
                                (!!data.totalValue || !!data.quotedTotalValue) &&
                                <a
                                    className="me-2"
                                    href={"javascript:void(0)"}
                                    onClick={(e) => { e.stopPropagation(); showQuote(data, allData); }}>
                                    <FontAwesomeIcon icon={faCartShopping as IconProp} /></a>
                            }
                        </td>)
                },
                sortable: false
            },
            createFilterableNumberRangeColumnDefinition<IOpportunity, IOpportunityFilters>(
                globalContext,
                "likleyhood",
                // @ts-ignore
                "likleyhood",
                true,
                filter,
                (value: any, row: any) => <td className="align-top" key={`likleyhood${row.healthRiskAssessmentId}`}>
                    {value ? value + "%" : ""}
                </td>,
                "% Closure"),
            createFilterableDateRangeColumnDefinition<IOpportunity, IOpportunityFilters>(
                globalContext,
                "closureDate",
                // @ts-ignore
                "closureDate",
                true,
                filter,
                (value, row) => <td className="align-top" key={`submissionDate${row.healthRiskAssessmentId}`}>
                    {getExpectedClosure(value)}</td>,
                "Expected"),
            createFilterableNumberRangeColumnDefinition<IOpportunity, IOpportunityFilters>(
                globalContext,
                "totalValue",
                // @ts-ignore
                "totalValue",
                true,
                filter,
                (value: any, row: any) => <td className="align-top" key={`totalValue${row.healthRiskAssessmentId}`}>
                    {formatCurrency(value ? parseFloat(value) : 0)}
                </td>,
                "Estimated Value"),
            createFilterableArrayColumnDefinition<IOpportunity, IOpportunityFilters>(
                globalContext,
                "stage",
                "stages",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`stage${row.opportunityId}`}>
                    {value}</td>,
                "Stage"),
            createFilterableArrayColumnDefinition<IOpportunity, IOpportunityFilters>(
                globalContext,
                // @ts-ignore
                "departmentName",
                "departmentNames",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`departmentNames${row.opportunityId}`}>
                    {row.fireDepartment?.fireDeptName}
                </td>,
                "Primary Location"),

            createFilterableArrayColumnDefinition<IOpportunity, IOpportunityFilters>(
                globalContext,
                // @ts-ignore
                "stationName",
                "stationNames",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`stationNames${row.opportunityId}`}>
                    {row.fireStation?.name}
                </td>,
                "Secondary Location"),
            {
                propName: "pocprimaryName",
                title: "Contact Name",
                customRenderer: (value, data) => <td className="align-top" key={`pocprimaryName${data.opportunityId}`}>{value}</td>,
                sortable: true
            },
            {
                propName: "meetingDate",
                title: "Next Meet",
                customRenderer: (value, data) => <td className="align-top" key={`meetingDate${data.opportunityId}`}>{formatDate(value)}</td>,
                sortable: true
            },
            createFilterableArrayColumnDefinition<IOpportunity, IOpportunityFilters>(
                globalContext,
                // @ts-ignore
                "dealerName",
                "dealerNames",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`dealerName${row.opportunityId}`}>
                    {row.dealer?.dealerName}
                </td>,
                "Dealer"),
            createFilterableArrayColumnDefinition<IOpportunity, IOpportunityFilters>(
                globalContext,
                // @ts-ignore
                "repName",
                "repNames",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`repName${row.opportunityId}`}>
                    {(!row.stage || row.stage === "Lead") && "New Lead / "}{
                        (row.salesRep ?
                            row.salesRep.fullName :
                            "No Rep Assigned")
                    }
                </td>,
                "Rep"),
            createFilterableArrayColumnDefinition<IOpportunity, IOpportunityFilters>(
                globalContext,
                // @ts-ignore
                "state",
                "states",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`state${row.opportunityId}`}>
                    {row.fireStation?.state ?? row.fireDepartment?.hqstate ?? ""}</td>,
                "State"),
            createFilterableArrayColumnDefinition<IOpportunity, IOpportunityFilters>(
                globalContext,
                "country",
                "countries",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`country${row.opportunityId}`}>
                    {row.fireStation?.country ?? row.fireDepartment?.hqCountry ?? row.country}</td>,
                "Country"),
            {
                propName: "opportunityId",
                title: "Source",
                customRenderer: (value, data) => <td className="align-top" key={`source${data.opportunityId}`}>
                    {data.airCardId && "AirCard"}
                    {data.airCardId && data.healthRiskAssessmentId && " & "}
                    {data.healthRiskAssessmentId && "HRA"}
                    {!data.airCardId && !data.healthRiskAssessmentId && "Manual"}
                </td>,
                sortable: false
            },
            {
                propName: "lastUpdated",
                title: "Last Update",
                customRenderer: (value, data) => <td className="align-top" key={`lastUpdated${data.opportunityId}`}>
                    {value ? formatDate(new Date(value)) : ""}
                </td>,
                sortable: true
            },
        ];
    };

    const rowStyleRenderer = (row: IOpportunity) => {
        let result = {};

        if (row.lastUpdated) {
            const diff =
                moment().diff(
                    moment(row.lastUpdated),
                    'days');

            if (diff > 35) {
                result = { backgroundColor: "rgba(255, 0, 0, 0.3)" };
            }
            else if (mode !== "Opportunities" && (row.airCard || row.healthRiskAssessment)) {
                if (
                    (row.airCard ? ((row.airCard.systemDataJson ?? "").indexOf("follow up email") > -1) : true) &&
                    (row.healthRiskAssessment ? ((row.healthRiskAssessment.systemDataJson ?? "").indexOf("follow up email") > -1) : true)
                ) {
                    result = { backgroundColor: "rgba(0, 255, 0, 0.1)" };
                }
                else {
                    result = { backgroundColor: "rgba(255, 0, 0, 0.1)" };
                }
            }
        }
        return result;
    };

    const getTools = () => [
        <>
            {
                globalContext.roles.some(r => r === "Web.Leads") && globalContext.roles.some(r => r === "Web.Opportunities") &&
                <Form.Select
                    style={{
                        "display": "inline",
                        "width": "130px",
                        "position": "relative",
                        "left": "-8px"
                    }}
                    className="float-start full-sm"
                    onChange={async (e) => {
                        setMode(e.target.value);
                    }}
                    value={mode}>
                    <option value={"Both"}>Both</option>
                    <option value={"Leads"}>Leads Only</option>
                    <option value={"Opportunities"}>Opp. Only</option>
                    <option value={"Closed"}>Closed</option>
                </Form.Select>
            }
        </>,
        <>
            {
                globalContext.roles.some(r => r === "Web.Opportunities") &&
                <LinkContainer to="/Tools/QuickQuote">
                    <Button
                        style={{
                            "display": "inline",
                            "width": "130px",
                            "position": "relative",
                            "left": "-8px"
                        }}
                        variant="primary"
                        className="ms-2 float-start full-sm"
                        onClick={() => {
                        }}>Quick Quote</Button>
                </LinkContainer>
            }
        </>,
        <Button
            className="me-2 full-sm"
            key="downloadExcel"
            onClick={() => { exportTableDataAsync(); }}>Export</Button>,
        <>
            {
                globalContext.roles.some(r => r === "Web.Admin") &&
                <Button
                    key="createnew"
                    variant="success"
                    className="full-sm"
                    onClick={() => { navigate(`/Opportunities/Detail/`) }}>Create New</Button>
            }
        </>
    ];

    return (
        <CommonLookupDisplay<IOpportunityFilters, IOpportunity, IOpportunityCreateOrUpdate>
            baseFilters={getBaseFilter()}
            filterHints={{
                minTotalValue: "string",
                maxTotalValue: "string",
                minQuotedTotalValue: "string",
                maxQuotedTotalValue: "string",
                minLikleyhood: "string",
                maxLikleyhood: "string",
                dealerNames: "Array",
                stationNames: "Array",
                departmentNames: "Array",
                repNames: "Array",
                leads: "Boolean",
                organizationTypes: "Array",
                hraIds: "Array",
                airCardIds: "Array",
                stages: "Array",
                states: "Array",
                countries: "Array",
                minClosureDate: "string",
                maxClosureDate: "string",
                sortBys: "Array",
                sortDescending: "Boolean"
            }}
            dataTypeName="Opportunity"
            dataTypeNamePlural="Opportunities"
            pathName="Opportunities"
            primaryIdKey="opportunityId"
            className=""
            countriesFilterKey={COUNTRIES_FILTER_KEY}
            statesFilterKey={STATES_FILTER_KEY}
            filter={filter}
            setFilter={setFilter}
            columnDefinitions={getColumns()}
            service={OpportunityService}
            tools={getTools()}
            rowStyleRenderer={rowStyleRenderer}
        />
    );
}

export default OpportunitiesLookup;
